<script setup lang="ts">
import type { ReceiveData } from '@/api/ai/aiType';
import type { Feedback } from '@/api/feedback/feedbackType';
import { Icon } from '@iconify/vue';
import { Plus } from 'lucide-vue-next';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { aiApi } from '@/api/ai/aiApi';
import { AI_SOURCE_CLASS_ANALYSIS } from '@/api/ai/sources';
import { fetchPostApi, fetchStudentClassListApi, fetchTeacherClassListApi, readPostApi } from '@/api/class/classApi';
import { fetchOfficialCourseReviewListApi } from '@/api/course/courseApi';
import { fetchQuestionApi } from '@/api/do-question/doQuestion';
import { editFeedbackApi, fetchFeedbackApi, postFeedbackApi } from '@/api/feedback/feedbackApi.ts';
import { subscribeToNotifications } from '@/api/notification/notificationStream';
import { fetchVisualizationReviewQueueApi } from '@/api/resource/visualizations';
import { putUserApi, putUserAvatarApi, updatePasswordApi } from '@/api/user/userApi.ts';
import About from '@/components/about/About.vue';
import { clearDynamicCourseContext } from '@/components/course/courseUtils';
import MemoryDashboard from '@/components/memory/MemoryDashboard.vue';
import { getVisualizationReviewDashboardUrl, openExternalNotificationDestination } from '@/components/notification/notificationNavigation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfigStore, useUserStore } from '@/store';
import { isNotificationPreviewEnabled, useNotificationStore } from '@/store/notification';
import { isPlatformAdminUser, isVisitor, logout } from '@/utils/auth.ts';
import { error, success, warning } from '@/utils/toast.ts';

const userStore = useUserStore();
const configStore = useConfigStore();
const notificationStore = useNotificationStore();
let notificationRefreshTimer: ReturnType<typeof setInterval> | undefined;
let notificationStream: AbortController | undefined;

const isLoading = ref(false);
const updateForm = reactive({
  account: '',
  realname: '',
  oldPassword: '',
  password: '',
  confirmPassword: '',
  school: '',
  grade: '',
  studentId: '',
  avatarUrl: '',
  email: '',
  phone: '',
  major: '',
});

const router = useRouter();
const route = useRoute();

function refreshNotifications() {
  if (!isNotificationPreviewEnabled(route.query.previewNotifications))
    void notificationStore.fetchNotifications();
}

const fileInput = ref<HTMLInputElement | null>(null);

async function onSubmit() {
  if (updateForm.realname === '') {
    warning('姓名不能为空');
    return false;
  }
  if (updateForm.school === '') {
    warning('就读学校不能为空');
    return false;
  }
  try {
    isLoading.value = true;
    const result = await putUserApi(
      updateForm.realname,
      0,
      updateForm.email || '',
      updateForm.phone || '',
      updateForm.major || '',
      userStore.user?.school || '',
      { college: updateForm.school, grade: updateForm.grade, studentId: updateForm.studentId },
    );
    userStore.user = result.user;
    success('个人资料已更新');
    isLoading.value = false;
    return true;
  }
  catch (e) {
    console.error('Error during updating user:', e);
    error('个人资料更新失败，请重试');
  }
  isLoading.value = false;
  return false;
}

async function updatePassword() {
  if (updateForm.oldPassword === '') {
    warning('旧密码不能为空');
    return false;
  }
  if (updateForm.password === '') {
    warning('新密码不能为空');
    return false;
  }
  if (updateForm.password !== updateForm.confirmPassword) {
    warning('两次输入的密码不一致');
    return false;
  }
  if (updateForm.oldPassword === updateForm.confirmPassword) {
    warning('新密码不能与旧密码相同');
    return false;
  }
  try {
    await updatePasswordApi(
      updateForm.oldPassword,
      updateForm.password,
    );
    success('密码已更新');
    return true;
  }
  catch (e: any) {
    console.error('Error during updating password:', e);
    if (e.response.status === 400) {
      warning('旧密码错误');
    }
    else {
      error('密码更新失败，请重试');
    }
  }
  return false;
}

function triggerFileUpload() {
  fileInput.value?.click();
}

async function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement;
  const files: FileList | null = target.files;
  if (files && files.length > 0) {
    const file: File = files[0];
    try {
      const result = await putUserAvatarApi(file);
      userStore.user = result.user;
      updateForm.avatarUrl = result.user.avatarUrl;
    }
    catch {
      console.error('Error during uploading avatar');
    }
  }
}

function openFeishuDoc() {
  window.open('https://ecng9aaaudb2.feishu.cn/wiki/LKLiwqUBciCZK7kqwZrcEOn5nJf?from=from_copylink', '_blank');
}

const avatarMenuOpen = ref(false);
const infoOpen = ref(false);
const profileTab = ref('profile');
const aboutOpen = ref(false);
const logoutOpen = ref(false);
const feedbackOpen = ref(false);
const unreadAnnouncementPostIds = ref<number[]>([]);
const loadingUnreadAnnouncements = ref(false);
const visualizationPendingReviewCount = ref(0);
const coursePendingReviewCount = ref(0);
const loadingPendingReview = ref(false);

const isAdmin = computed(() => isPlatformAdminUser(userStore.user));
const pendingReviewCount = computed(() => visualizationPendingReviewCount.value + coursePendingReviewCount.value);
const notificationUnreadCount = computed(() => notificationStore.unreadCount);
const avatarBadgeCount = computed(() => notificationUnreadCount.value || (isAdmin.value ? pendingReviewCount.value : 0));

function openProfileDialog() {
  if (!userStore.user)
    return;
  updateForm.avatarUrl = userStore.user.avatarUrl;
  updateForm.account = userStore.user.displayUsername || userStore.user.username;
  updateForm.realname = userStore.user.realName;
  updateForm.school = userStore.user.college || '';
  updateForm.grade = String(userStore.user.grade || '');
  updateForm.studentId = userStore.user.studentId || userStore.user.buptUsername || '';
  updateForm.email = userStore.user.email ?? '';
  updateForm.phone = userStore.user.phone ?? '';
  updateForm.major = userStore.user.major ?? '';
  updateForm.oldPassword = '';
  updateForm.password = '';
  updateForm.confirmPassword = '';
  profileTab.value = 'profile';
  infoOpen.value = true;
}

async function refreshPendingReviewCount() {
  if (!isAdmin.value)
    return;
  loadingPendingReview.value = true;
  try {
    const [visualizations, courses] = await Promise.all([
      fetchVisualizationReviewQueueApi(),
      fetchOfficialCourseReviewListApi('pending'),
    ]);
    visualizationPendingReviewCount.value = visualizations.length;
    coursePendingReviewCount.value = courses.counts?.pending ?? courses.courses.length;
  }
  catch {
    visualizationPendingReviewCount.value = 0;
    coursePendingReviewCount.value = 0;
  }
  finally { loadingPendingReview.value = false; }
}

function openWechatBindPage() {
  avatarMenuOpen.value = false;
  router.push('/wechat/bind');
}

function openMembershipPage() {
  avatarMenuOpen.value = false;
  router.push('/membership');
}

function openMyCoursesPage() {
  avatarMenuOpen.value = false;
  configStore.dynamicSubject = null;
  clearDynamicCourseContext();
  router.push('/my-courses');
}

function openNotificationsPage() {
  avatarMenuOpen.value = false;
  const preview = isAdmin.value ? 'admin' : Number(userStore.user?.role) === 2 ? 'teacher' : 'student';
  const shouldUsePreview = import.meta.env.DEV && !localStorage.getItem('token');
  router.push({
    path: '/notifications',
    query: {
      returnTo: route.fullPath,
      ...(shouldUsePreview ? { preview, previewNotifications: '1' } : {}),
    },
  });
}

function returnToPortalHome() {
  avatarMenuOpen.value = false;
  configStore.currentSubjectId = null;
  configStore.dynamicSubject = null;
  clearDynamicCourseContext();
  router.push('/');
}

function openReviewPage(resource: 'visualization' | 'public-course') {
  avatarMenuOpen.value = false;
  if (resource === 'public-course') {
    router.push('/admin/course-reviews');
    return;
  }
  openExternalNotificationDestination(getVisualizationReviewDashboardUrl());
}

const isStudentUser = computed(() => {
  const role = userStore.user?.role;
  return Number(role) === 1;
});

const hasUnreadClassAnnouncement = computed(() => {
  return isStudentUser.value && unreadAnnouncementPostIds.value.length > 0;
});

function normalizePostId(post: any): number | null {
  const raw = post?.id ?? post?.postId;
  const id = Number(raw);
  if (!Number.isFinite(id) || id <= 0)
    return null;
  return id;
}
function normalizeTeachingClassIds(post: any): number[] {
  const ids = Array.isArray(post?.teachingClassIds) ? post.teachingClassIds : [];
  return ids
    .map((id: unknown) => Number(id))
    .filter((id: number) => Number.isFinite(id) && id > 0);
}

function isPostInAllowedClasses(post: any, allowedClassIdSet: Set<number>): boolean {
  const ids = normalizeTeachingClassIds(post);
  if (!ids.length)
    return true;
  return ids.some(id => allowedClassIdSet.has(id));
}

function normalizeReadFlag(post: any): boolean | null {
  const flag = post?.isRead;
  if (flag === true || flag === 1 || flag === '1' || flag === 'true')
    return true;
  if (flag === false || flag === 0 || flag === '0' || flag === 'false')
    return false;
  return null;
}

function extractUnreadPostIdsFromResult(result: any): number[] {
  const posts = Array.isArray(result?.posts) ? result.posts : [];

  return posts
    .map((post: any) => {
      const id = normalizePostId(post);
      if (!id)
        return null;

      const explicit = normalizeReadFlag(post);
      if (explicit === false)
        return id;
      if (explicit === true)
        return null;
      return null;
    })
    .filter((id: number | null): id is number => id !== null);
}

async function refreshUnreadAnnouncements() {
  if (!isStudentUser.value) {
    unreadAnnouncementPostIds.value = [];
    return;
  }

  if (loadingUnreadAnnouncements.value)
    return;

  loadingUnreadAnnouncements.value = true;
  try {
    const { teachingClasses } = await fetchStudentClassListApi();
    const classes = teachingClasses ?? [];
    if (!classes.length) {
      unreadAnnouncementPostIds.value = [];
      return;
    }

    const classIds = classes.map(c => c.id).filter(Boolean);
    if (!classIds.length) {
      unreadAnnouncementPostIds.value = [];
      return;
    }
    const allowedClassIdSet = new Set<number>(
      classIds
        .map((id: unknown) => Number(id))
        .filter((id: number) => Number.isFinite(id) && id > 0),
    );

    const postResult = await fetchPostApi(classIds);
    const allPosts = Array.isArray((postResult as any)?.posts) ? (postResult as any).posts : [];
    const visiblePosts = allPosts.filter((post: any) => isPostInAllowedClasses(post, allowedClassIdSet));
    const unreadIds = extractUnreadPostIdsFromResult({ ...(postResult as any), posts: visiblePosts });

    unreadAnnouncementPostIds.value = Array.from(new Set(unreadIds));
  }
  catch (e) {
    console.error('加载未读公告失败', e);
    unreadAnnouncementPostIds.value = [];
  }
  finally {
    loadingUnreadAnnouncements.value = false;
  }
}

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function syncReadStatusFromServer(submittedIds: number[]) {
  const targetIds = Array.from(new Set(submittedIds));
  if (!targetIds.length)
    return;

  // Retry a few times because backend read-status may be eventually consistent.
  for (let i = 0; i < 3; i++) {
    await refreshUnreadAnnouncements();
    const stillUnread = unreadAnnouncementPostIds.value.filter((id: number) => targetIds.includes(id));
    if (!stillUnread.length)
      return;
    await wait(300);
  }

  const stillUnread = unreadAnnouncementPostIds.value.filter((id: number) => targetIds.includes(id));
  if (stillUnread.length) {
    console.error('后端已读状态未更新', { submittedIds: targetIds, stillUnreadIds: stillUnread });
  }
}

async function markAnnouncementsAsRead() {
  const ids = Array.from(new Set(unreadAnnouncementPostIds.value));
  if (!ids.length)
    return;
  try {
    await readPostApi(ids);
    await syncReadStatusFromServer(ids);
  }
  catch (e) {
    console.error('公告已读提交失败', e);
  }
}

async function handleOpenClassDialog() {
  avatarMenuOpen.value = false;
  if (hasUnreadClassAnnouncement.value) {
    await markAnnouncementsAsRead();
  }
  await router.push('/class-management');
}

const seeFeedback = ref(false);
const feedback = ref('improvement');
const content = ref('');
const feedbackList = ref<Feedback[] | null>(null);
const teacherClasses = ref<any[]>([]);
const teacherYearOptions = ref<string[]>([]);
const teacherSelectedYear = ref<string>('');
const teacherSelectedClassId = ref<string>('');
const teacherFeedbacks = ref<Array<{ id?: string | number; realName: string; content: string; timestamp: string; review_status?: string; teaching_class?: any; question_number?: string; question_id?: string | number }>>([]);
const teacherSummary = ref('');
const teacherSummaryLoading = ref(false);
const teacherSummaryError = ref('');
const showMyHistoryModal = ref(false);
const myFeedbackHistory = ref<Array<any>>([]);
// admin view state
const adminView = ref(false);
const adminImprovements = ref<Array<any>>([]);
const adminQuestions = ref<Array<any>>([]);
const adminQuestionBaseUrl = 'https://umathhub.com/backend-dashboard/assessment/question/';
const frontQuestionBasePath = '/question/';
// floating teacher panel state
// dialog drag state for feedback dialog (teacher view)
const feedbackDialogRef = ref<HTMLElement | null>(null);
const dialogPos = ref({ x: 0, y: 0 });
const dialogPosSet = ref(false);
const dialogDragging = ref(false);
const dialogOffset = ref({ x: 0, y: 0 });
const dialogPointerId = ref<number | null>(null);
let attachedDialogEl: HTMLElement | null = null;
let attachedHeaderEl: HTMLElement | null = null;
let rafId: number | null = null;
let pendingClientX = 0;
let pendingClientY = 0;

function findDialogElement(): HTMLElement | null {
  const list = Array.from(document.querySelectorAll<HTMLElement>('.shadow-lg'));
  if (list.length === 0)
    return null;
  return list[list.length - 1];
}

function attachDragListeners() {
  // find dialog DOM
  const dialogEl = findDialogElement();
  if (!dialogEl)
    return;
  attachedDialogEl = dialogEl;
  // apply current pos if set
  if (dialogPosSet.value) {
    dialogEl.style.position = 'fixed';
    dialogEl.style.left = '0';
    dialogEl.style.top = '0';
    dialogEl.style.margin = '0';
    dialogEl.style.transform = `translate3d(${dialogPos.value.x}px, ${dialogPos.value.y}px, 0)`;
  }

  // header
  const header = dialogEl.querySelector<HTMLElement>('.draggable-header');
  if (header) {
    attachedHeaderEl = header;
    header.style.touchAction = 'none';
    header.style.cursor = dialogDragging.value ? 'grabbing' : 'grab';
    header.addEventListener('pointerdown', onDialogPointerDown as any);
  }
}

function detachDragListeners() {
  try {
    if (attachedHeaderEl) {
      attachedHeaderEl.removeEventListener('pointerdown', onDialogPointerDown as any);
      attachedHeaderEl.style.touchAction = '';
      attachedHeaderEl.style.cursor = '';
      attachedHeaderEl = null;
    }
  }
  catch (_e) {}
  try {
    if (attachedDialogEl) {
      // clear inlined styles we set
      if (dialogPosSet.value === false) {
        attachedDialogEl.style.position = '';
        attachedDialogEl.style.left = '';
        attachedDialogEl.style.top = '';
        attachedDialogEl.style.margin = '';
        attachedDialogEl.style.transform = '';
      }
      attachedDialogEl = null;
    }
  }
  catch (_e) {}
}

// update position styles when dialogPos changes
watch(dialogPos, () => {
  if (attachedDialogEl && dialogPosSet.value) {
    attachedDialogEl.style.position = 'fixed';
    attachedDialogEl.style.left = '0';
    attachedDialogEl.style.top = '0';
    attachedDialogEl.style.transform = `translate3d(${dialogPos.value.x}px, ${dialogPos.value.y}px, 0)`;
  }
}, { deep: true });

// attach/detach when feedback view opens/closes
watch([feedbackOpen, seeFeedback], async ([open, see]) => {
  if (open && see && userStore.user?.role === 2) {
    // ensure DOM rendered before attaching drag listeners
    await nextTick();
    attachDragListeners();
  }
  else {
    detachDragListeners();
  }
});

function reviewLabel(status?: string) {
  const s = (status || '').toString().toLowerCase();
  if (s === 'pending')
    return '处理中';
  if (s === 'approved')
    return '已解决';
  if (s === 'rejected')
    return '已拒绝';
  return '';
}

function reviewClass(status?: string) {
  const s = (status || '').toString().toLowerCase();
  if (s === 'pending')
    return 'bg-yellow-100 text-yellow-800';
  if (s === 'approved')
    return 'bg-green-100 text-green-800';
  if (s === 'rejected')
    return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-700';
}

const reviewStatusDebounceMs = 1000;
const pendingReviewTimers = new Map<string | number, number>();

function scheduleReviewStatusSync(feedbackId: string | number | undefined, nextStatus?: string) {
  if (!feedbackId)
    return;
  if (!nextStatus)
    return;
  const existing = pendingReviewTimers.get(feedbackId);
  if (existing) {
    window.clearTimeout(existing);
  }
  const timer = window.setTimeout(async () => {
    pendingReviewTimers.delete(feedbackId);
    try {
      await editFeedbackApi(String(feedbackId), nextStatus);
    }
    catch (err) {
      console.error('反馈状态同步失败', err);
      error('反馈状态同步失败，请稍后重试');
    }
  }, reviewStatusDebounceMs);
  pendingReviewTimers.set(feedbackId, timer);
}

function nextReviewStatus(current?: string) {
  const s = (current || '').toString().toLowerCase();
  if (s === 'pending')
    return 'approved';
  if (s === 'approved')
    return 'rejected';
  return 'pending';
}

function cycleAdminQuestionStatus(index: number) {
  if (!isAdminUser())
    return;
  const item = adminQuestions.value[index];
  if (!item)
    return;
  const nextStatus = nextReviewStatus(item.review_status);
  item.review_status = nextStatus;
  scheduleReviewStatusSync(item.id, nextStatus);
}

function cycleTeacherQuestionStatus(index: number) {
  if (!isAdminUser())
    return;
  const item = teacherFeedbacks.value[index];
  if (!item)
    return;
  const nextStatus = nextReviewStatus(item.review_status);
  item.review_status = nextStatus;
  scheduleReviewStatusSync(item.id, nextStatus);
}

function parseQuestionNumber(qn?: string) {
  if (!qn)
    return '';
  const m = /^([A-Z]+)(\d{7})$/.exec(qn.toString().trim());
  if (!m)
    return qn;
  const prefix = m[1];
  const digits = m[2];
  const chapter = Number(digits.slice(0, 2));
  const section = Number(digits.slice(2, 4));
  const qnum = Number(digits.slice(4, 7));

  const subjects: Record<string, string> = {
    PT: '概率论',
    LA: '线性代数',
    NT: '数论',
    AMI: '高等数学（上）',
    AMII: '高等数学（下）',
    S: '统计决策与贝叶斯分析',
    BLA: '双语线性代数',
    RT: '风险理论',
  };

  const subject = subjects[prefix] ?? prefix;

  function toChinese(n: number) {
    if (!Number.isFinite(n) || n <= 0)
      return String(n);
    const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
    if (n < 10)
      return digits[n];
    if (n < 20) {
      if (n === 10)
        return '十';
      return `十${digits[n - 10]}`;
    }
    if (n < 100) {
      const t = Math.floor(n / 10);
      const r = n % 10;
      return r === 0 ? `${digits[t]}十` : `${digits[t]}十${digits[r]}`;
    }
    return String(n);
  }

  const chapZh = toChinese(chapter);
  const secStr = String(section);

  return `${subject} 第${chapZh}章 第${secStr}节 第${qnum}题`;
}

function parseQuestionIndex(qn?: string): number | null {
  if (!qn)
    return null;
  const match = /(\d{3})$/.exec(String(qn).trim());
  if (!match)
    return null;
  const raw = Number(match[1]);
  if (!Number.isFinite(raw) || raw <= 0)
    return null;
  return raw - 1;
}

function extractFeedbackItems(payload: any): any[] {
  const listAny: any = payload ?? {};
  const candidates = (listAny.feedbacks ?? listAny.feedback ?? (listAny.data && listAny.data.feedbacks) ?? listAny) as any[];
  return Array.isArray(candidates) ? candidates : [];
}

async function loadMyFeedbackHistory() {
  if (isVisitor()) {
    return;
  }
  try {
    const resp = await fetchFeedbackApi({ category: 'improvement', mine: true });
    const mapped = extractFeedbackItems(resp).sort((a: any, b: any) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    }).map((f: any) => ({
      feedbackID: f.feedbackID ?? f.feedbackId ?? f.id,
      timestamp: f.timestamp ?? f.createdAt ?? '',
      content: f.content ?? f.body ?? '',
      review_status: f.review_status ?? f.reviewStatus ?? '',
      className: (
        (f.teaching_class && (f.teaching_class.name || f.teaching_class.title))
        || (f.admin_class && (f.admin_class.name || f.admin_class.title))
        || (f.teachingClass && (f.teachingClass.name || f.teachingClass.title))
        || (f['teaching class'] && (f['teaching class'].name || f['teaching class'].title))
        || (f.class && (f.class.name || f.class.title))
        || ''
      ),
      // keep mapping minimal for my feedback history (no class/question display)
    }));
    myFeedbackHistory.value = mapped;
    showMyHistoryModal.value = true;
  }
  catch (err) {
    console.error('加载网页反馈历史失败', err);
    myFeedbackHistory.value = [];
    showMyHistoryModal.value = true;
  }
}
async function refreshFeedback() {
  if (isVisitor()) {
    return;
  }
  try {
    const result = await fetchFeedbackApi({ category: 'improvement', mine: true });
    feedbackList.value = extractFeedbackItems(result) as any;
  }
  catch (error) {
    console.error('Error during fetching feedbacks:', error);
  }
}

async function loadTeacherClasses() {
  try {
    const resp = await fetchTeacherClassListApi();
    teacherClasses.value = (resp as any)?.teachingClasses || [];
    // extract year options from class createdAt first; fallback to id/code parsing
    const yearsSet = new Set<string>();
    teacherClasses.value.forEach((c: any) => {
      const created = c.createdAt ?? c.created_at ?? c.created ?? '';
      let year = '';
      if (created) {
        const m = String(created).match(/^(\d{4})/);
        if (m) {
          year = m[1];
        }
        else {
          const d = new Date(created);
          if (!Number.isNaN(d.getTime()))
            year = String(d.getFullYear());
        }
      }

      if (year) {
        yearsSet.add(year);
        return;
      }

      // fallback: try to parse from id/code (older format)
      const idStr = String(c.id ?? c.code ?? '');
      const m4 = idStr.match(/^(20\d{2})/);
      if (m4) {
        yearsSet.add(m4[0]);
      }
      else {
        const m2 = idStr.match(/^(\d{2})/);
        if (m2)
          yearsSet.add(`20${m2[1]}`);
      }
    });
    const years = Array.from(yearsSet).map(y => y).sort((a, b) => Number(b) - Number(a));
    teacherYearOptions.value = years;
    teacherSelectedYear.value = years.length ? years[0] : '';

    // choose first class in selected year
    const filtered = teacherClasses.value.filter((c: any) => {
      if (!teacherSelectedYear.value)
        return true;
      const created = c.createdAt ?? c.created_at ?? c.created ?? '';
      if (created) {
        const m = String(created).match(/^(\d{4})/);
        if (m && m[1] === teacherSelectedYear.value)
          return true;
        const d = new Date(created);
        if (!Number.isNaN(d.getTime()) && String(d.getFullYear()) === teacherSelectedYear.value)
          return true;
      }
      const idStr = String(c.id ?? c.code ?? '');
      return idStr.startsWith(teacherSelectedYear.value) || idStr.startsWith(teacherSelectedYear.value.slice(2));
    });
    teacherSelectedClassId.value = filtered.length ? String((filtered[0] as any).id) : '';
    if (teacherSelectedClassId.value) {
      await loadTeacherFeedbacks(teacherSelectedClassId.value);
    }
  }
  catch (err) {
    console.error('加载教师教学班失败', err);
    teacherClasses.value = [];
    teacherSelectedClassId.value = '';
    teacherFeedbacks.value = [];
  }
}

const filteredTeacherClasses = computed(() => {
  if (!teacherSelectedYear.value)
    return teacherClasses.value;
  return teacherClasses.value.filter((c: any) => {
    const created = c.createdAt ?? c.created_at ?? c.created ?? '';
    if (created) {
      const m = String(created).match(/^(\d{4})/);
      if (m && m[1] === teacherSelectedYear.value)
        return true;
      const d = new Date(created);
      if (!Number.isNaN(d.getTime()) && String(d.getFullYear()) === teacherSelectedYear.value)
        return true;
    }
    const idStr = String(c.id ?? c.code ?? '');
    return idStr.startsWith(teacherSelectedYear.value) || idStr.startsWith(teacherSelectedYear.value.slice(2));
  });
});

watch(teacherSelectedYear, (_ny) => {
  // when year changes, pick first class in that year and load feedbacks
  const filtered = filteredTeacherClasses.value;
  teacherSelectedClassId.value = filtered.length ? String((filtered[0] as any).id) : '';
  if (teacherSelectedClassId.value) {
    loadTeacherFeedbacks(teacherSelectedClassId.value).catch(e => console.error('加载教师反馈失败', e));
  }
  else {
    teacherFeedbacks.value = [];
  }
});

async function loadTeacherFeedbacks(classId: string) {
  try {
    const resp = await fetchFeedbackApi({ category: 'question', teaching_class_id: classId });
    const mapped = extractFeedbackItems(resp).sort((a: any, b: any) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    }).map((f: any) => ({
      id: f.feedbackID ?? f.feedbackId ?? f.id,
      realName: f.real_name ?? f.realName ?? f.user?.realName ?? f.user?.real_name ?? f.username ?? '',
      content: f.content ?? f.body ?? '',
      timestamp: f.timestamp ?? f.createdAt ?? '',
      review_status: f.review_status ?? f.reviewStatus ?? '',
      // preserve teaching_class and question_number for teacher view display
      teaching_class: f.teaching_class ?? f.teachingClass ?? f.class ?? undefined,
      question_number: f.question_number ?? f.questionNumber ?? '',
      question_id: f.question_id ?? f.questionId ?? f.question?.id ?? f.question?.question_id ?? '',
    }));
    teacherFeedbacks.value = mapped;
    teacherSummary.value = '';
    teacherSummaryError.value = '';
  }
  catch (err) {
    console.error('加载教师反馈失败', err);
    teacherFeedbacks.value = [];
  }
}

async function generateTeacherFeedbackSummary() {
  const classId = teacherSelectedClassId.value;
  if (!classId || teacherFeedbacks.value.length === 0) {
    teacherSummaryError.value = teacherFeedbacks.value.length === 0 ? '当前教学班暂无题目反馈可供总结' : '请先选择教学班';
    teacherSummary.value = '';
    return;
  }
  teacherSummaryLoading.value = true;
  teacherSummaryError.value = '';
  teacherSummary.value = '';
  const feedbacks = teacherFeedbacks.value.map(item => ({
    student: item.realName || '学生',
    content: item.content,
    question: item.question_number ? parseQuestionNumber(item.question_number) : undefined,
  }));
  const system = {
    role: 'system' as const,
    content: '你是数学课程教师的教学分析助手。请根据学生提交的题目反馈，做宏观、匿名、可行动的班级问题总结。识别反复出现的知识主题、理解障碍和题型/方法问题，避免逐条复述、避免推测单个学生，不要编造数据。使用 Markdown，严格输出三个小节：### 主要问题（3-5条）### 可能原因（2-4条）### 教学建议（3-5条）。总字数控制在300-600字。',
  };
  const user = {
    role: 'user' as const,
    content: JSON.stringify({ task: '总结本教学班所有学生的题目反馈，侧重宏观共性问题', classId, feedbackCount: feedbacks.length, feedbacks }),
  };
  try {
    await new Promise<void>((resolve, reject) => {
      aiApi({ messages: [system, user], source: AI_SOURCE_CLASS_ANALYSIS }, () => {}, (data: ReceiveData) => {
        if (data.type === 'text')
          teacherSummary.value += data.content || '';
      }, result => result?.hasValidText ? resolve() : reject(new Error('AI response unavailable')));
    });
  }
  catch (err) {
    console.error('生成班级反馈总结失败', err);
    teacherSummary.value = '';
    teacherSummaryError.value = 'AI 总结暂时不可用，请稍后重试';
  }
  finally {
    teacherSummaryLoading.value = false;
  }
}

function openTeacherFeedback() {
  // open teacher feedback view inside the dialog; allow dragging via header
  seeFeedback.value = true;
  loadTeacherClasses().catch(e => console.error('教师反馈加载失败', e));
}
function onDialogPointerDown(e: PointerEvent) {
  // Only start dragging when pointerdown originates from the draggable header.
  const tgt = e.target as HTMLElement | null;
  const headerEl = tgt?.closest('.draggable-header') as HTMLElement | null;
  if (!headerEl)
    return;
  e.preventDefault();
  console.log('onDialogPointerDown', { clientX: e.clientX, clientY: e.clientY, pointerId: e.pointerId, target: (e.target as any)?.tagName });
  const refVal: any = feedbackDialogRef.value;
  // find a real DOM element for the dialog container
  let el: HTMLElement | null = null;
  try {
    const ct = e.currentTarget as HTMLElement | null;
    if (ct && typeof ct.getBoundingClientRect === 'function') {
      el = ct;
    }
  }
  catch (_e) {
    // ignore
  }
  if (!el && refVal) {
    try {
      if (refVal.$el && typeof refVal.$el.getBoundingClientRect === 'function')
        el = refVal.$el as HTMLElement;
      else if (typeof refVal.getBoundingClientRect === 'function')
        el = refVal as HTMLElement;
      else if (refVal.$el && refVal.$el.$el && typeof refVal.$el.$el.getBoundingClientRect === 'function')
        el = refVal.$el.$el as HTMLElement;
    }
    catch (_e) {
      // ignore
    }
  }
  if (!el) {
    const dialogClosest = tgt?.closest('[role="dialog"]') ?? document.querySelector('.shadow-lg');
    if (dialogClosest && typeof dialogClosest.getBoundingClientRect === 'function')
      el = dialogClosest as HTMLElement;
  }
  if (!el)
    return;
  // attempt to capture pointer to receive subsequent events reliably
  try {
    if (typeof (el as any).setPointerCapture === 'function') {
      (el as any).setPointerCapture(e.pointerId);
      dialogPointerId.value = e.pointerId;
    }
  }
  catch (_e) {
    // ignore
  }
  // also try to capture on the original event target for better reliability
  try {
    const t = e.target as HTMLElement | null;
    if (t && typeof (t as any).setPointerCapture === 'function') {
      (t as any).setPointerCapture(e.pointerId);
      dialogPointerId.value = e.pointerId;
    }
  }
  catch (_e) {
    // ignore
  }

  // prevent text selection during drag
  try {
    document.body.style.userSelect = 'none';
  }
  catch (_e) {
    // ignore
  }

  const rect = el.getBoundingClientRect();
  // initialize dialogPos if not yet set
  if (!dialogPosSet.value) {
    dialogPos.value.x = rect.left;
    dialogPos.value.y = rect.top;
    dialogPosSet.value = true;
  }
  dialogDragging.value = true;
  dialogOffset.value.x = e.clientX - dialogPos.value.x;
  dialogOffset.value.y = e.clientY - dialogPos.value.y;
}

function onWindowPointerMove(e: PointerEvent) {
  if (!dialogDragging.value)
    return;
  pendingClientX = e.clientX;
  pendingClientY = e.clientY;
  if (rafId !== null)
    return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    const newX = Math.max(10, pendingClientX - dialogOffset.value.x);
    const newY = Math.max(10, pendingClientY - dialogOffset.value.y);
    dialogPos.value.x = newX;
    dialogPos.value.y = newY;
    if (attachedDialogEl && dialogPosSet.value) {
      attachedDialogEl.style.transform = `translate3d(${newX}px, ${newY}px, 0)`;
    }
    if (attachedHeaderEl) {
      attachedHeaderEl.style.cursor = 'grabbing';
    }
  });
}

function onWindowMouseUp() {
  dialogDragging.value = false;
  // release pointer capture if any
  const refVal: any = feedbackDialogRef.value;
  const el: HTMLElement | null = (refVal && (refVal.$el || refVal)) ?? null;
  if (el && dialogPointerId.value !== null) {
    try {
      if (typeof (el as any).releasePointerCapture === 'function') {
        (el as any).releasePointerCapture(dialogPointerId.value);
      }
    }
    catch (_e) {
      // ignore
    }
    dialogPointerId.value = null;
  }
  try {
    document.body.style.userSelect = '';
  }
  catch (_e) {
    // ignore
  }
}

onMounted(() => {
  // support both pointer and mouse events for broad compatibility
  window.addEventListener('pointermove', onWindowPointerMove);
  window.addEventListener('pointerup', onWindowMouseUp);
  // existing init
  refreshFeedback();
  refreshNotifications();
  window.addEventListener('focus', refreshNotifications);
  notificationRefreshTimer = setInterval(refreshNotifications, 60_000);
  if (!isNotificationPreviewEnabled(route.query.previewNotifications))
    notificationStream = subscribeToNotifications(refreshNotifications);
  refreshUnreadAnnouncements().catch(e => console.error('初始化未读公告失败', e));
  refreshPendingReviewCount().catch(e => console.error('初始化待审核数量失败', e));
});

onUnmounted(() => {
  window.removeEventListener('pointermove', onWindowPointerMove);
  window.removeEventListener('pointerup', onWindowMouseUp);
  window.removeEventListener('focus', refreshNotifications);
  if (notificationRefreshTimer)
    clearInterval(notificationRefreshTimer);
  notificationStream?.abort();
});

watch(
  () => [
    Number(userStore.user?.role ?? 0),
    isAdmin.value,
    route.query.previewNotifications,
  ] as const,
  ([role, admin, explicitPreview]) => {
    const previewEnabled = isNotificationPreviewEnabled(explicitPreview);
    notificationStore.loadPreview(
      role,
      admin,
      previewEnabled,
    );
    if (!previewEnabled)
      void notificationStore.fetchNotifications();
  },
  { immediate: true },
);

watch(() => configStore.currentSubjectId, () => {
  refreshUnreadAnnouncements().catch(e => console.error('学科切换后未读公告刷新失败', e));
});

watch(() => route.fullPath, () => {
  refreshUnreadAnnouncements().catch(e => console.error('路由切换后未读公告刷新失败', e));
});

watch(avatarMenuOpen, (open) => {
  if (open) {
    refreshUnreadAnnouncements().catch(e => console.error('头像菜单打开时未读公告刷新失败', e));
    refreshPendingReviewCount().catch(e => console.error('待审核数量加载失败', e));
  }
});

function closeTeacherFeedback() {
  seeFeedback.value = false;
  teacherFeedbacks.value = [];
  teacherSelectedClassId.value = '';
  // reset admin view if any
  adminView.value = false;
  adminImprovements.value = [];
  adminQuestions.value = [];
  teacherSummary.value = '';
  teacherSummaryError.value = '';
}

function isAdminUser(): boolean {
  return isPlatformAdminUser(userStore.user);
}
function openAdminQuestion(questionId?: number | string) {
  if (!isAdminUser())
    return;
  if (!questionId)
    return;
  const id = Number(questionId);
  if (!Number.isFinite(id) || id <= 0)
    return;
  const url = `${adminQuestionBaseUrl}${id}/change/`;
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function resolveTeachingClassId(payload: any): string | null {
  const rawClass = payload?.teaching_class ?? payload?.teachingClass ?? payload?.class;
  const rawId = rawClass?.id ?? rawClass?.classId ?? rawClass?.teachingClassId;
  const normalized = rawId ?? payload?.teaching_class_id ?? payload?.teachingClassId ?? payload?.classId;
  const fallback = teacherSelectedClassId.value || '';
  const value = normalized ?? fallback;
  if (!value)
    return null;
  const id = String(value);
  return id || null;
}

async function openFrontQuestion(payload: any) {
  const rawQuestionId = payload?.question_id ?? payload?.questionId ?? payload?.question?.id ?? payload?.question?.question_id;
  if (!rawQuestionId)
    return;
  const questionIndex = parseQuestionIndex(payload?.question_number ?? payload?.questionNumber);
  if (questionIndex === null)
    return;

  try {
    const { question } = await fetchQuestionApi(String(rawQuestionId));
    const chapterId = (question as any)?.chapter?.id ?? (question as any)?.chapter_id;
    if (!chapterId)
      return;
    const classId = resolveTeachingClassId(payload);
    await router.push({
      path: `${frontQuestionBasePath}${chapterId}`,
      query: {
        questionIndex: String(questionIndex),
        ...(classId ? { classId } : {}),
      },
    });
  }
  catch (err) {
    console.error('打开题目链接失败', err);
  }
}

async function handleQuestionJump(payload: any) {
  if (!payload)
    return;
  if (isAdminUser())
    openAdminQuestion(payload?.question_id ?? payload?.questionId ?? payload?.question?.id ?? payload?.question?.question_id);
  await openFrontQuestion(payload);
}

async function openAdminFeedback() {
  // open admin view and load all feedbacks, split by category
  if (!isAdminUser())
    return;
  try {
    const [improvementResp, questionResp] = await Promise.all([
      fetchFeedbackApi({ category: 'improvement' }),
      fetchFeedbackApi({ category: 'question' }),
    ]);
    const improvements = extractFeedbackItems(improvementResp);
    const questions = extractFeedbackItems(questionResp);
    adminImprovements.value = improvements.map((f: any) => ({
      id: f.feedbackID ?? f.feedbackId ?? f.id,
      timestamp: f.timestamp ?? f.createdAt ?? '',
      content: f.content ?? f.body ?? '',
      review_status: f.review_status ?? f.reviewStatus ?? '',
      // prefer real name when available
      user: f.user?.realName ?? f.user?.real_name ?? f.real_name ?? f.username ?? f.user?.username ?? f.userName ?? '',
    }));
    adminQuestions.value = questions.map((f: any) => ({
      id: f.feedbackID ?? f.feedbackId ?? f.id,
      timestamp: f.timestamp ?? f.createdAt ?? '',
      content: f.content ?? f.body ?? '',
      review_status: f.review_status ?? f.reviewStatus ?? '',
      // prefer real name when available
      user: f.user?.realName ?? f.user?.real_name ?? f.real_name ?? f.username ?? f.user?.username ?? f.userName ?? '',
      teaching_class: f.teaching_class ?? f.teachingClass ?? f.class ?? undefined,
      question_number: f.question_number ?? f.questionNumber ?? '',
      question_id: f.question_id ?? f.questionId ?? f.question?.id ?? f.question?.question_id ?? '',
    }));
    adminView.value = true;
    seeFeedback.value = true;
  }
  catch (err) {
    console.error('加载全部反馈失败', err);
    adminImprovements.value = [];
    adminQuestions.value = [];
    adminView.value = false;
  }
}

async function sendFeedback() {
  if (isVisitor()) {
    warning('请先登录');
    return;
  }
  if (content.value === '') {
    warning('意见反馈不能为空');
    return;
  }
  try {
    await postFeedbackApi(feedback.value, content.value);
    await refreshFeedback();
    success('反馈成功，感谢您的支持！');
    content.value = '';
  }
  catch (e: any) {
    console.error(e);
  }
}

async function handleSave() {
  if ((updateForm.oldPassword !== '' || updateForm.password !== '' || updateForm.confirmPassword !== '') && userStore.user?.source !== 0) {
    const ok = await updatePassword();
    if (!ok)
      return;
  }

  const ok2 = await onSubmit();
  if (!ok2)
    return;

  infoOpen.value = false;
}

// removed duplicate onMounted; initialization handled above

// 关闭反馈对话框时重置为初始输入页面
watch(feedbackOpen, (val) => {
  if (!val) {
    seeFeedback.value = false;
    showMyHistoryModal.value = false;
    teacherFeedbacks.value = [];
    teacherSelectedClassId.value = '';
    myFeedbackHistory.value = [];
  }
});
</script>

<template>
  <div v-if="userStore.user">
    <DropdownMenu v-model:open="avatarMenuOpen">
      <DropdownMenuTrigger class="flex items-center justify-center">
        <div class="relative">
          <Avatar class="border-2 size-10">
            <AvatarImage :src="userStore.user.avatarUrl" />
            <AvatarFallback>{{ (userStore.user.realName || userStore.user.username || '我').slice(0, 1) }}</AvatarFallback>
          </Avatar>
          <span
            v-if="hasUnreadClassAnnouncement && avatarBadgeCount === 0"
            class="absolute -right-0.5 -top-0.5 inline-block w-2.5 h-2.5 rounded-full bg-red-500 ring-1 ring-background z-20"
          />
          <span
            v-if="avatarBadgeCount > 0"
            class="absolute -right-1 -top-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold ring-1 ring-background z-20 px-1"
          >
            {{ avatarBadgeCount > 99 ? '99+' : avatarBadgeCount }}
          </span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="w-64">
        <div class="flex gap-2 p-2 items-center">
          <Avatar class="size-12 border-2">
            <AvatarImage :src="userStore.user.avatarUrl" />
            <AvatarFallback>userStore.user.username</AvatarFallback>
          </Avatar>
          <div class="flex flex-col">
            <div class="font-semibold">
              {{ userStore.user.realName }}
            </div>
            <div class="text-muted-foreground">
              {{ userStore.user.displayUsername || userStore.user.username }}
            </div>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem class="p-2 cursor-pointer" @click="openProfileDialog">
          <Icon icon="lucide:user" class="size-4" />
          个人信息
        </DropdownMenuItem>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="handleOpenClassDialog">
          <Icon icon="lucide:users" class="size-4" />
          <span class="flex items-center justify-between w-full">
            <span>班级管理</span>
            <span
              v-if="hasUnreadClassAnnouncement"
              class="inline-block w-2 h-2 rounded-full bg-red-500 ring-1 ring-background z-20"
            />
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="openMyCoursesPage">
          <Icon icon="lucide:book-marked" class="size-4" />
          我的课程
        </DropdownMenuItem>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="openNotificationsPage">
          <Icon icon="lucide:bell" class="size-4 text-indigo-600" />
          <span class="flex items-center justify-between w-full pr-6">
            <span>通知中心</span>
            <span
              v-if="notificationUnreadCount > 0"
              class="inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white"
            >
              {{ notificationUnreadCount > 99 ? '99+' : notificationUnreadCount }}
            </span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuSub v-if="isAdmin">
          <DropdownMenuSubTrigger class="p-2 cursor-pointer">
            <Icon icon="lucide:shield-alert" class="mr-2 size-4 text-red-500" />
            <span class="flex items-center justify-between w-full">
              <span>待审核资源</span>
              <span v-if="pendingReviewCount > 0" class="mr-2 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-red-500 text-white text-xs font-bold px-1">{{ pendingReviewCount > 99 ? '99+' : pendingReviewCount }}</span>
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent class="w-64">
            <DropdownMenuItem class="p-3 cursor-pointer" @click="openReviewPage('visualization')">
              <Icon icon="lucide:panels-top-left" class="size-4 text-indigo-500" />
              <span class="flex items-center justify-between w-full">
                <span>可视化审核</span>
                <span
                  v-if="visualizationPendingReviewCount > 0"
                  class="inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                >
                  {{ visualizationPendingReviewCount > 99 ? '99+' : visualizationPendingReviewCount }}
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem class="p-3 cursor-pointer" @click="openReviewPage('public-course')">
              <Icon icon="lucide:book-check" class="size-4 text-emerald-600" />
              <span class="flex items-center justify-between w-full">
                <span>课程公开审核</span>
                <span
                  v-if="coursePendingReviewCount > 0"
                  class="inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                >
                  {{ coursePendingReviewCount > 99 ? '99+' : coursePendingReviewCount }}
                </span>
              </span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="openWechatBindPage">
          <Icon icon="lucide:landmark" class="size-4 text-blue-600" />
          {{ userStore.user?.buptAuthenticated ? '北邮认证已绑定' : '绑定北邮认证' }}
        </DropdownMenuItem>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="openMembershipPage">
          <Icon icon="lucide:badge-dollar-sign" class="size-4 text-blue-600" />
          会员充值
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem class="p-2 cursor-pointer" @click="feedbackOpen = true">
          <Icon icon="lucide:circle-help" class="size-4" />
          问题反馈
        </DropdownMenuItem>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="openFeishuDoc">
          <Icon icon="lucide:book-open" class="size-4" />
          用户手册
        </DropdownMenuItem>
        <DropdownMenuItem
          class="p-2 cursor-pointer" @click="aboutOpen = true"
        >
          <Icon icon="lucide:aperture" class="size-4" />
          关于我们
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          class="p-2 cursor-pointer"
          :disabled="configStore.currentSubjectId === null && !configStore.dynamicSubject"
          @click="returnToPortalHome"
        >
          <Icon icon="lucide:undo-2" class="size-4" />
          返回主页
        </DropdownMenuItem>
        <DropdownMenuItem class="p-2 cursor-pointer" @click="logoutOpen = true">
          <Icon icon="lucide:log-out" class="size-4" />
          退出登录
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <Dialog v-model:open="infoOpen">
      <DialogContent class="max-h-[85vh] w-[calc(100vw-2rem)] max-w-5xl overflow-y-auto p-6 sm:p-8">
        <DialogHeader>
          <DialogTitle>个人中心</DialogTitle>
          <DialogDescription>
            管理个人资料，并查看 AI 助教使用的学习记忆。
          </DialogDescription>
        </DialogHeader>

        <Tabs v-model="profileTab" class="mt-2">
          <TabsList class="grid h-10 w-full max-w-sm grid-cols-2">
            <TabsTrigger value="profile">
              个人资料
            </TabsTrigger>
            <TabsTrigger value="memory">
              记忆看板
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" class="mt-6">
            <div class="grid grid-cols-1 gap-10 md:grid-cols-2">
              <div class="space-y-5">
                <div class="relative">
                  <Input
                    id="username"
                    :model-value="updateForm.account"
                    disabled
                    class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                  />
                  <Label
                    for="username"
                    class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                  >
                    账号
                  </Label>
                </div>

                <div class="relative">
                  <Input
                    id="realname"
                    v-model="updateForm.realname"
                    type="text"
                    placeholder="真实姓名"
                    class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                  />
                  <Label
                    for="realname"
                    class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                  >
                    真实姓名
                  </Label>
                </div>

                <div class="relative">
                  <Input
                    id="schoolProfile"
                    v-model="updateForm.school"
                    :disabled="isLoading"
                    placeholder="所属院校"
                    class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                  />
                  <Label
                    for="schoolProfile"
                    class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                  >
                    所属院校
                  </Label>
                </div>

                <div class="space-y-2">
                  <Label for="gradeProfile">年级</Label>
                  <Input id="gradeProfile" v-model="updateForm.grade" maxlength="20" :disabled="isLoading" />
                  <Label for="studentIdProfile">学号</Label>
                  <Input id="studentIdProfile" v-model="updateForm.studentId" maxlength="50" :disabled="isLoading || userStore.user?.buptAuthenticated" />
                </div>
                <div class="relative">
                  <Input
                    id="emailProfile"
                    v-model="updateForm.email"
                    :disabled="isLoading"
                    type="email"
                    placeholder="邮箱（可选）"
                    class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                  />
                  <Label
                    for="emailProfile"
                    class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                  >
                    邮箱（可选）
                  </Label>
                </div>

                <div class="relative">
                  <Input
                    id="phoneProfile"
                    v-model="updateForm.phone"
                    disabled
                    type="tel"
                    placeholder="暂未绑定"
                    class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl opacity-60 cursor-not-allowed"
                  />
                  <Label
                    for="phoneProfile"
                    class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                  >
                    手机号（只读，需单独换绑）
                  </Label>
                </div>

                <div class="relative">
                  <Input
                    id="majorProfile"
                    v-model="updateForm.major"
                    :disabled="isLoading"
                    placeholder="专业（非大学生或大学老师填无）"
                    class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                  />
                  <Label
                    for="majorProfile"
                    class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                  >
                    专业（非大学生或大学老师填无）
                  </Label>
                </div>

                <div v-if="userStore.user?.source !== 0" class="pt-2">
                  <div class="text-sm font-medium mb-2">
                    安全设置
                  </div>

                  <div class="relative mb-4">
                    <Input
                      id="oldPassword"
                      v-model="updateForm.oldPassword"
                      type="password"
                      autocomplete="off"
                      placeholder="旧密码"
                      class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                    />
                    <Label
                      for="oldPassword"
                      class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                  peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                  -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                    >
                      旧密码
                    </Label>
                  </div>

                  <div class="relative mb-4">
                    <Input
                      id="newPassword"
                      v-model="updateForm.password"
                      type="password"
                      autocomplete="new-password"
                      placeholder="新密码"
                      class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                    />
                    <Label
                      for="newPassword"
                      class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                  peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                  -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                    >
                      新密码
                    </Label>
                  </div>

                  <div class="relative">
                    <Input
                      id="confirmPassword"
                      v-model="updateForm.confirmPassword"
                      type="password"
                      autocomplete="new-password"
                      placeholder="确认密码"
                      class="peer h-12 w-full pt-2 !placeholder-transparent rounded-xl"
                    />
                    <Label
                      for="confirmPassword"
                      class="pointer-events-none absolute left-3 top-0 text-muted-foreground transition-all
                  peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm
                  -translate-y-1/2 text-sm bg-background px-1 peer-focus:text-primary peer-focus:top-0"
                    >
                      确认密码
                    </Label>
                  </div>
                </div>
              </div>

              <div class="grid gap-3 md:self-start">
                <Label>头像</Label>
                <div class="flex justify-center items-start">
                  <Avatar class="size-64 relative">
                    <AvatarImage :src="updateForm.avatarUrl" alt="avatar" />
                    <Button
                      variant="ghost"
                      class="absolute top-0 left-0 size-64 rounded-full opacity-0 transition-all hover:opacity-100 hover:bg-black/70"
                      @click="triggerFileUpload"
                    >
                      <div class="flex flex-col items-center text-background">
                        <Plus class="size-6" />
                        <div class="font-semibold">
                          上传图片
                        </div>
                      </div>
                    </Button>
                  </Avatar>
                </div>
                <input ref="fileInput" type="file" class="hidden" accept="image/*" @change="handleFileUpload">
              </div>
            </div>

            <DialogFooter class="mt-6 md:gap-5">
              <Button @click="handleSave">
                保存设置
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="memory" force-mount class="mt-6">
            <MemoryDashboard :active="profileTab === 'memory'" />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    <!-- 历史视图已集成到反馈对话框内，移除独立弹窗 -->

    <AlertDialog v-model:open="logoutOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>确定要退出登录吗？</AlertDialogTitle>
          <AlertDialogDescription>
            退出后需要重新登录才能继续使用。
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            @click="() => {
              logoutOpen = false;
              logout();
              router.push('/login');
            }"
          >
            确认
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Dialog v-model:open="feedbackOpen" class="overflow-y-auto">
      <DialogContent
        ref="feedbackDialogRef"
        class="overflow-y-auto p-6 w-[30vw] max-w-none h-[82vh] border border-gray-300 rounded-lg"
        :no-overlay="seeFeedback && userStore.user?.role === 2"
        @pointer-down-outside="(event) => {
          if (seeFeedback && (adminView || userStore.user?.role === 2)) {
            event.preventDefault();
          }
        }"
      >
        <DialogHeader class="draggable-header" @pointerdown.stop>
          <DialogTitle>问题反馈</DialogTitle>
          <DialogDescription v-if="seeFeedback === false">
            如果您在学习时遇到了任何问题，或者有任何希望改进本软件的建议，请随时告诉我们。
            课堂上您提出的学习问题会由老师统一答复，而关于软件的反馈，我们的开发团队会及时调整和优化，确保更好地满足您的需求！期待您的宝贵意见！
          </DialogDescription>
        </DialogHeader>
        <FloatLabel v-if="seeFeedback === false && !showMyHistoryModal">
          <Textarea v-model="content" class="resize-none min-h-[50vh]" :rows="15" placeholder="请在这里输入您的问题反馈！" />
        </FloatLabel>

        <!-- 历史记录在此区域显示，替换输入框（仅当 showMyHistoryModal 为 true 且 非教师查看模式） -->
        <div v-if="seeFeedback === false && showMyHistoryModal" class="h-[50vh] p-4 border rounded overflow-auto pb-20">
          <div class="mb-3 text-sm text-muted-foreground">
            网页反馈历史
          </div>
          <div class="space-y-3">
            <div v-if="myFeedbackHistory.length === 0" class="text-sm text-muted-foreground">
              暂无网页反馈历史
            </div>
            <div v-else>
              <div v-for="(f, idx) in myFeedbackHistory" :key="f.feedbackID || idx" class="bg-white rounded-lg p-4 shadow-sm">
                <div class="text-xs text-muted-foreground mb-2">
                  {{ f.timestamp ? new Date(f.timestamp).toISOString().slice(0, 10) : '' }}
                </div>
                <div class="flex items-start justify-between">
                  <div class="mt-0 text-sm">
                    {{ f.content }}
                  </div>
                  <div class="ml-4 flex-shrink-0">
                    <span class="px-2 py-1 rounded-full text-xs" :class="[reviewClass(f.review_status)]">{{ reviewLabel(f.review_status) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="seeFeedback === true">
          <div v-if="adminView" class="h-[60vh] p-4 border rounded overflow-hidden">
            <div class="mb-3 text-sm text-muted-foreground">
              全部反馈
            </div>
            <div class="space-y-4 overflow-auto h-[46vh] pr-2 pb-20">
              <div>
                <div class="text-sm font-medium mb-2">
                  improvement反馈
                </div>
                <div v-if="adminImprovements.length === 0" class="text-sm text-muted-foreground">
                  暂无improvement反馈
                </div>
                <div v-else class="space-y-3">
                  <div v-for="(a, i) in adminImprovements" :key="a.id || i" class="rounded-2xl p-4 bg-white shadow-sm border border-gray-100">
                    <div class="text-sm font-medium">
                      {{ a.user }}
                    </div>
                    <div class="text-xs text-muted-foreground mb-2">
                      {{ a.timestamp ? new Date(a.timestamp).toISOString().slice(0, 10) : '' }}
                    </div>
                    <div class="flex items-start justify-between gap-3">
                      <div class="text-sm">
                        {{ a.content }}
                      </div>
                      <div class="flex-shrink-0">
                        <span class="px-2 py-1 rounded-full text-xs" :class="[reviewClass(a.review_status)]">{{ reviewLabel(a.review_status) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div class="text-sm font-medium mb-2">
                  question反馈
                </div>
                <div v-if="adminQuestions.length === 0" class="text-sm text-muted-foreground">
                  暂无question反馈
                </div>
                <div v-else class="space-y-3">
                  <div
                    v-for="(a, i) in adminQuestions"
                    :key="a.id || i"
                    class="rounded-2xl p-4 bg-white shadow-sm border border-gray-100"
                    :class="a.question_id && a.question_number ? 'cursor-pointer hover:shadow-md' : ''"
                    @click="handleQuestionJump(a)"
                  >
                    <div class="text-sm font-medium">
                      {{ a.user }}
                    </div>
                    <div class="flex items-center justify-between text-xs text-muted-foreground mb-2">
                      <div>{{ a.timestamp ? new Date(a.timestamp).toISOString().slice(0, 10) : '' }}</div>
                      <div v-if="a.teaching_class || a.question_number" class="flex items-center gap-1 text-right">
                        <span v-if="a.teaching_class && a.teaching_class.name">{{ a.teaching_class.name }}</span>
                        <span v-if="a.teaching_class && a.teaching_class.name && a.question_number"> · </span>
                        <span v-if="a.question_number">{{ parseQuestionNumber(a.question_number) }}</span>
                      </div>
                    </div>
                    <div class="flex items-start justify-between gap-3">
                      <div class="text-sm">
                        {{ a.content }}
                      </div>
                      <div class="flex-shrink-0">
                        <button
                          type="button"
                          class="px-2 py-1 rounded-full text-xs"
                          :class="[reviewClass(a.review_status), isAdminUser() ? 'cursor-pointer hover:opacity-80' : 'cursor-default']"
                          :disabled="!isAdminUser()"
                          @click.stop="cycleAdminQuestionStatus(i)"
                        >
                          {{ reviewLabel(a.review_status) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="userStore.user?.role === 2 && !adminView" class="h-[60vh] p-4 border rounded overflow-hidden">
            <div class="flex items-center justify-between mb-3">
              <div class="mb-0 text-sm text-muted-foreground">
                请选择教学班查看该班级的题目反馈：
              </div>
              <div class="flex items-center">
                <label class="text-sm text-muted-foreground mr-2">年份：</label>
                <select v-model="teacherSelectedYear" class="border rounded-full px-3 h-8 text-sm shadow-sm">
                  <option v-for="y in teacherYearOptions" :key="y" :value="y">
                    {{ y }}
                  </option>
                </select>
              </div>
            </div>
            <div class="flex gap-3 overflow-x-auto pb-3">
              <button
                v-for="cls in filteredTeacherClasses"
                :key="cls.id"
                class="px-4 py-2 rounded-md border text-sm"
                :class="{ 'bg-primary text-white': String(cls.id) === String(teacherSelectedClassId) }"
                @click="() => { teacherSelectedClassId = String(cls.id); loadTeacherFeedbacks(String(cls.id)); }"
              >
                {{ cls.name || cls.title || cls.code || cls.id }}
              </button>
            </div>
            <div class="mb-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-3">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <div class="text-sm font-medium text-indigo-950">
                    AI 班级问题总结
                  </div>
                  <div class="mt-1 text-xs text-indigo-700/80">
                    根据当前教学班的全部题目反馈，归纳共性学习障碍与教学方向
                  </div>
                </div>
                <Button size="sm" :disabled="teacherSummaryLoading || teacherFeedbacks.length === 0" @click="generateTeacherFeedbackSummary">
                  {{ teacherSummaryLoading ? '正在总结…' : '生成总结' }}
                </Button>
              </div>
              <div v-if="teacherSummaryError" class="mt-2 text-xs text-red-600">
                {{ teacherSummaryError }}
              </div>
              <div v-if="teacherSummary" class="mt-3 max-h-56 overflow-auto rounded-lg border border-indigo-100 bg-white p-3 text-sm leading-6 whitespace-pre-wrap text-slate-700">
                {{ teacherSummary }}
              </div>
            </div>
            <div class="overflow-auto space-y-3 mt-3 h-[46vh] pr-2 pb-20">
              <div v-if="teacherFeedbacks.length === 0" class="text-sm text-muted-foreground">
                该班暂无题目反馈
              </div>
              <div v-else>
                <div
                  v-for="(t, idx) in teacherFeedbacks"
                  :key="idx"
                  class="rounded-2xl p-4 bg-white shadow-sm border border-gray-100 transition-shadow"
                  :class="t.question_id && t.question_number ? 'cursor-pointer hover:shadow-md' : ''"
                  @click="handleQuestionJump(t)"
                >
                  <div class="text-sm font-medium">
                    {{ t.realName }}
                  </div>
                  <div class="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <div>{{ t.timestamp ? new Date(t.timestamp).toISOString().slice(0, 10) : '' }}</div>
                    <div v-if="t.teaching_class || t.question_number" class="flex items-center gap-1 text-right">
                      <span v-if="t.teaching_class && t.teaching_class.name">{{ t.teaching_class.name }}</span>
                      <span v-if="t.teaching_class && t.teaching_class.name && t.question_number"> · </span>
                      <span v-if="t.question_number">{{ parseQuestionNumber(t.question_number) }}</span>
                    </div>
                  </div>
                  <div class="flex items-start justify-between">
                    <div class="mt-0 text-sm">
                      {{ t.content }}
                    </div>
                    <div class="ml-4 flex-shrink-0">
                      <button
                        type="button"
                        class="px-2 py-1 rounded-full text-xs"
                        :class="[reviewClass(t.review_status), isAdminUser() ? 'cursor-pointer hover:opacity-80' : 'cursor-default']"
                        :disabled="!isAdminUser()"
                        @click.stop="cycleTeacherQuestionStatus(idx)"
                      >
                        {{ reviewLabel(t.review_status) }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-for="feed in feedbackList" :key="feed.feedbackID">
              <div class="border py-3">
                {{ feed.content }}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter class="flex justify-between items-center">
          <div>
            <Button v-if="isAdminUser() && seeFeedback === false && !showMyHistoryModal" variant="outline" class="mr-2 px-4 py-2" @click="openAdminFeedback">
              全部反馈
            </Button>
            <Button v-if="userStore.user?.role === 2 && seeFeedback === false && !showMyHistoryModal" variant="outline" class="px-4 py-2" @click="openTeacherFeedback">
              教学班题目反馈
            </Button>
          </div>
          <div class="flex items-center gap-2">
            <!-- 教师查看模式的返回 -->
            <Button v-if="seeFeedback === true" variant="outline" class="px-4 py-2" @click="closeTeacherFeedback">
              返回
            </Button>

            <!-- 历史查看时的返回（在同一对话框内） -->
            <Button v-if="showMyHistoryModal && seeFeedback === false" variant="outline" class="px-4 py-2" @click="showMyHistoryModal = false">
              返回
            </Button>

            <!-- 普通输入模式下的查看历史按钮 -->
            <Button v-if="!showMyHistoryModal && seeFeedback === false" variant="outline" class="px-4 py-2" @click="loadMyFeedbackHistory">
              网页反馈历史
            </Button>

            <DialogClose>
              <Button v-if="!showMyHistoryModal && seeFeedback === false" class="px-4 py-2" @click="sendFeedback">
                提交
              </Button>
            </DialogClose>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog v-model:open="aboutOpen">
      <DialogContent class="max-w-screen-lg mx-auto">
        <DialogHeader>
          <DialogTitle>
            关于我们
          </DialogTitle>
        </DialogHeader>
        <About />
      </DialogContent>
    </Dialog>
  </div>
</template>
